/* @flow */
import { combineReducers } from 'redux-immutable';
import { Map, fromJS } from 'immutable';
import type { Action } from 'trustar/utils/redux-types.js';
import createDebugger from 'debug';

const debug = createDebugger('app:modules:create:stores'); // eslint-disable-line no-unused-vars

import {
  SET_STAGE,
  SET_SUBMISSION_TYPE,
  REVIEW_REPORT,
  REVIEW_REPORT_SUCCESS,
  REVIEW_REPORT_FAILURE,
  SUBMIT_REPORT,
  SUBMIT_REPORT_SUCCESS,
  SUBMIT_REPORT_FAILURE,
  UPDATE_REPORT,
  UPDATE_REPORT_SUCCESS,
  UPDATE_REPORT_FAILURE,
  REPORT_PROCESSED,
  REPORT_POLLING_FAILURE,
  ATTACH_FILE,
  ATTACH_FILE_FAILURE,
  ATTACH_INDICATORS_SPREADSHEET_FILE,
  ATTACH_INDICATORS_SPREADSHEET_FILE_FAILURE,
  ATTACH_INDICATORS_SPREADSHEET_FILE_SUCCESS,
  SUBMIT_INDICATORS_SUCCESS,
  SUBMIT_INDICATORS_FAILURE,
  UPLOAD_FILE_SUCCESS,
  UPLOAD_FILE_FAILURE,
  TOGGLE_REDACTION,
  REGISTER_FORM,
  SET_FIELD_VALUE,
  UNREGISTER_FORM,
  LOAD_EXISTING_REPORT_CONTENT,
} from './action-types.js';
import { Form, toForm } from './utils';
import type { SubmissionType } from './constants';

// Store for the Create Report Form Content, legacy from the Forms Module
const form = (state: typeof Form = Form(), action: Action<*>) => {
  const { type, payload } = action;
  switch (type) {
  case LOAD_EXISTING_REPORT_CONTENT:
    return fromJS({ currentValues: payload.currentValues, enclaveToCopyTo: payload.enclaveToCopyTo }, toForm);
  case REGISTER_FORM:
    return fromJS({ currentValues: payload.values || Map() }, toForm);
  case SET_FIELD_VALUE:
    const { value, keyPath } = payload;
    return state.setIn(['currentValues', ...keyPath], value);
  case UNREGISTER_FORM:
  case SUBMIT_REPORT_SUCCESS:
  case SUBMIT_INDICATORS_SUCCESS:
  case UPDATE_REPORT_SUCCESS:
    return Form();
  default:
    return state;
  }
};

/**
 * There is no initial stage, which means the splash page is where the user
 * should end up.
 */
const currentStage = (state: ?string = null, action: Action<*>) => {
  const { type, payload } = action;

  switch (type) {
  case SET_STAGE:
    return payload;
  case SUBMIT_REPORT_SUCCESS:
  case UPDATE_REPORT_SUCCESS:
  case REGISTER_FORM:
    return 'setup';
  case SUBMIT_INDICATORS_SUCCESS:
    return '';
  case SUBMIT_INDICATORS_FAILURE:
  case SUBMIT_REPORT_FAILURE:
  case UPDATE_REPORT_FAILURE:
  case ATTACH_INDICATORS_SPREADSHEET_FILE_SUCCESS:
    return 'review';
  default:
    return state;
  }
};

/**
 * Stores submission type for IOC submission: SPREADSHEET | FILE | TEXT
 */
const submissionType = (state: ?SubmissionType = null, action: Action<*>) => {
  const { type, payload } = action;

  switch (type) {
  case SET_SUBMISSION_TYPE:
    return payload;
  case REGISTER_FORM:
    return '';
  default:
    return state;
  }
};

const applyRedaction = (state: boolean = true, action: Action<*>) => {
  const { type } = action;

  switch (type) {
  case TOGGLE_REDACTION:
    return !state;
  default:
    return state;
  }
};

/* Loading state for the attach file dropzone */
const attachFileLoading = (state: boolean = false, action: Action<*>) => {
  const { type } = action;
  switch (type) {
  case ATTACH_FILE:
  case ATTACH_INDICATORS_SPREADSHEET_FILE:
    return true;
  case ATTACH_FILE_FAILURE:
  case ATTACH_INDICATORS_SPREADSHEET_FILE_SUCCESS:
  case ATTACH_INDICATORS_SPREADSHEET_FILE_FAILURE:
  case UPLOAD_FILE_SUCCESS:
  case UPLOAD_FILE_FAILURE:
    return false;
  default:
    return state;
  }
};

/* Loading state for the report review stage */
const loading = (state: boolean | string = false, action: Action<*>) => {
  const { type } = action;

  switch (type) {
  case REVIEW_REPORT:
    return true;
  case SUBMIT_REPORT:
  case UPDATE_REPORT:
    return 'submission';
  case REGISTER_FORM:
  case UNREGISTER_FORM:
  case REVIEW_REPORT_SUCCESS:
  case REVIEW_REPORT_FAILURE:
  case SUBMIT_REPORT_FAILURE:
  case UPDATE_REPORT_FAILURE:
    return false;
  default:
    return state;
  }
};

export default combineReducers({
  form,
  currentStage,
  submissionType,
  applyRedaction,
  attachFileLoading,
  loading,
});



// WEBPACK FOOTER //
// ./src/app/modules/create/stores.js